// Issue creation and deletion, independent of which workspace is active (#238).
//
// Only these two, because they are the issue writes a cross-workspace surface
// performs: the Dashboard converts a to-do into an issue, and undoing that
// takes the issue away again. Editing and resolving still belong to the
// workspace that owns them.
import { applyWrite } from '@/shared/lib/store-write';
import type { Issue } from '@/modules/issues/types';

import type { MutationContext } from '@/modules/activity/mutation-context';

/**
 * The issue as it will be written: homed, statused, and with "me" resolved to a
 * concrete person.
 *
 * `ownerId` may still be absent afterwards — the caller decides whether to look
 * one up before inserting or to write an unowned issue.
 */
export function prepareIssue(ctx: MutationContext, issue: Issue): Issue {
  const status = issue.status ?? (issue.resolved ? 'resolved' : 'open');
  return {
    ...issue,
    teamId: issue.teamId ?? ctx.defaultTeamId(),
    status,
    resolved: status === 'resolved',
    ownerId: ctx.resolveOwnerId(issue.ownerId) ?? issue.ownerId,
  };
}

export function insertIssue(
  ctx: MutationContext,
  issue: Issue,
  hooks: { rollback(): void },
): void {
  applyWrite(ctx.api('/records/issues', {
    ...(issue.teamId ? { team_id: issue.teamId } : {}),
    id: issue.id, title: issue.title, description: issue.description ?? null,
    owner_id: issue.ownerId ?? null, priority: issue.priority,
    resolved: issue.status === 'resolved', status: issue.status,
    created_at_date: issue.createdAt,
    year: issue.year ?? null, quarter: issue.quarter ?? null,
    rock_id: issue.rockId ?? null,
    process_id: issue.processId ?? null,
    process_step_id: issue.processStepId ?? null,
    insight_id: issue.insightId ?? null,
    workspace_id: ctx.workspaceId,
    unlocked_cycle: ctx.unlockedCycleKey?.() ?? null,
  }), {
    noun: 'this issue',
    context: 'addIssue',
    rollback: hooks.rollback,
  });
}

/** Create an issue whose owner is already settled. */
export function addIssue(
  ctx: MutationContext,
  issue: Issue,
  hooks: { apply(issue: Issue): void; rollback(): void },
): void {
  if (ctx.isBlocked(issue.year, issue.quarter)) return;
  const final = prepareIssue(ctx, issue);
  hooks.apply(final);
  insertIssue(ctx, final, hooks);
}

/**
 * Delete an issue.
 *
 * The audit entry and the owner's notification are the same ones the workspace
 * writes, so an issue removed from the Dashboard leaves the same trail as one
 * removed where it lives.
 */
export function deleteIssue(
  ctx: MutationContext,
  id: string,
  before: Pick<Issue, 'title' | 'ownerId' | 'year' | 'quarter'> | undefined,
  hooks: { apply(): void },
): void {
  if (before && ctx.isBlocked(before.year, before.quarter)) return;
  hooks.apply();

  applyWrite(ctx.api(`/records/issues/${id}`, { unlocked_cycle: ctx.unlockedCycleKey?.() ?? null }, 'DELETE'), {
    noun: 'this issue',
    context: 'deleteIssue',
    operation: 'delete',
    refetch: ctx.refetch,
  });
}
